import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Slider } from "../ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { ProposalEntityPicker } from "./ProposalEntityPicker";
import { toast } from "sonner";
import { Loader2, Sparkles, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const EMPTY_PROPOSAL = {
  title: "", linea_estrategica: "", core_proyecto: "",
  objetivos_beneficios: "", buenas_practicas: "", riesgos: "", cronograma: "", equipo_necesario: "",
  convocatoria: { query_or_url: "", organismo: "", nombre: "", plazos: "", condiciones: "", presupuesto_maximo: "", porcentaje_subvencion: "", url: "", filled_by_ai: false },
  budget: { coste_total: "", opex_pct: 50, capex_pct: 50, notas: "" },
  linked_entities: [], notes: "",
};

const STEPS = ["general", "contenido", "convocatoria", "presupuesto", "revision"];
const STEP_LABELS = { general: "General", contenido: "Contenido", convocatoria: "Convocatoria", presupuesto: "Presupuesto", revision: "Revisión" };

export function ProposalWizard({ open, onOpenChange, editing, entities, onSaved }) {
  const { token } = useAuth();
  const [form, setForm] = useState(EMPTY_PROPOSAL);
  const [step, setStep] = useState("general");
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [referenceText, setReferenceText] = useState("");

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        ...EMPTY_PROPOSAL, ...editing,
        convocatoria: { ...EMPTY_PROPOSAL.convocatoria, ...(editing.convocatoria || {}) },
        budget: {
          ...EMPTY_PROPOSAL.budget, ...(editing.budget || {}),
          coste_total: editing.budget?.coste_total ?? "",
        },
      } : EMPTY_PROPOSAL);
      setStep("general");
      setReferenceText("");
    }
  }, [open, editing]);

  const set = (field, val) => setForm((f) => ({ ...f, [field]: val }));
  const setConv = (field, val) => setForm((f) => ({ ...f, convocatoria: { ...f.convocatoria, [field]: val } }));
  const setBudget = (field, val) => setForm((f) => ({ ...f, budget: { ...f.budget, [field]: val } }));

  const stepIndex = STEPS.indexOf(step);
  const goNext = () => setStep(STEPS[Math.min(stepIndex + 1, STEPS.length - 1)]);
  const goBack = () => setStep(STEPS[Math.max(stepIndex - 1, 0)]);

  const handleSearchConvocatoria = async () => {
    if (!form.convocatoria.query_or_url.trim()) {
      toast.error("Escribe una URL o el nombre de la convocatoria/ayuda");
      return;
    }
    setSearching(true);
    try {
      const result = await api.searchConvocatoriaAI(token, form.convocatoria.query_or_url.trim());
      setForm((f) => ({ ...f, convocatoria: { ...f.convocatoria, ...result } }));
      toast.success("Datos de la convocatoria extraídos — revísalos antes de guardar");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo extraer la información. Rellénalo manualmente.");
    } finally {
      setSearching(false);
    }
  };

  const handleAiDraft = async () => {
    if (!form.title.trim()) {
      toast.error("Indica al menos el título antes de generar el borrador");
      return;
    }
    setDrafting(true);
    try {
      const result = await api.aiDraftProposal(token, {
        title: form.title, linea_estrategica: form.linea_estrategica, core_proyecto: form.core_proyecto,
        convocatoria: form.convocatoria, linked_entities: form.linked_entities, reference_text: referenceText,
      });
      setForm((f) => ({
        ...f,
        core_proyecto: result.core_proyecto || f.core_proyecto,
        objetivos_beneficios: result.objetivos_beneficios || f.objetivos_beneficios,
        buenas_practicas: result.buenas_practicas || f.buenas_practicas,
        riesgos: result.riesgos || f.riesgos,
        cronograma: result.cronograma || f.cronograma,
        equipo_necesario: result.equipo_necesario || f.equipo_necesario,
        budget: {
          ...f.budget,
          opex_pct: result.opex_pct ?? f.budget.opex_pct,
          capex_pct: result.capex_pct ?? f.budget.capex_pct,
          notas: result.opex_capex_rationale || f.budget.notas,
        },
        convocatoria: {
          ...f.convocatoria,
          organismo: result.convocatoria_organismo || f.convocatoria.organismo,
          nombre: result.convocatoria_nombre || f.convocatoria.nombre,
          plazos: result.convocatoria_plazos || f.convocatoria.plazos,
          condiciones: result.convocatoria_condiciones || f.convocatoria.condiciones,
          presupuesto_maximo: result.convocatoria_presupuesto_maximo || f.convocatoria.presupuesto_maximo,
        },
      }));
      toast.success("Borrador generado con IA — revísalo y ajusta lo que necesites");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "La IA no pudo generar el borrador");
    } finally {
      setDrafting(false);
    }
  };

  const buildPayload = () => ({
    ...form,
    budget: { ...form.budget, coste_total: form.budget.coste_total === "" ? null : parseFloat(form.budget.coste_total) },
  });

  const handleSave = async (sendForReview) => {
    if (!form.title.trim()) {
      toast.error("El título es obligatorio");
      setStep("general");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const saved = editing?.id
        ? await api.updateProposal(token, editing.id, payload)
        : await api.createProposal(token, payload);
      if (sendForReview) {
        await api.sendProposalForReview(token, saved.id);
        toast.success("Propuesta enviada a revisión de David Redondo");
      } else {
        toast.success(editing?.id ? "Propuesta actualizada" : "Propuesta guardada como borrador");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar la propuesta");
    } finally {
      setSaving(false);
    }
  };

  const opexPct = Number(form.budget.opex_pct ?? 50);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[92vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="proposal-wizard-dialog">
        <DialogHeader>
          <DialogTitle>{editing?.id ? "Editar propuesta" : "Nueva ficha de propuesta"}</DialogTitle>
          <DialogDescription>Genera una ficha de propuesta con branding corporativo para presentar internamente o a una convocatoria.</DialogDescription>
        </DialogHeader>

        <Tabs value={step} onValueChange={setStep} className="min-w-0">
          <TabsList className="flex min-w-0 w-full justify-start overflow-x-auto sm:grid sm:grid-cols-5">
            {STEPS.map((s) => <TabsTrigger key={s} value={s} className="shrink-0 whitespace-nowrap text-xs sm:text-sm" data-testid={`proposal-step-${s}`}>{STEP_LABELS[s]}</TabsTrigger>)}
          </TabsList>

          <TabsContent value="general" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Título de la propuesta *</Label>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Ej: Planta piloto de reciclaje textil" data-testid="proposal-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Línea estratégica</Label>
              <Input value={form.linea_estrategica} onChange={(e) => set("linea_estrategica", e.target.value)} placeholder="Ej: Economía circular / Descarbonización" data-testid="proposal-linea-input" />
            </div>
            <div className="space-y-2">
              <Label>Entidades vinculadas</Label>
              <ProposalEntityPicker entities={entities} value={form.linked_entities} onChange={(v) => set("linked_entities", v)} testId="proposal-entities-picker" />
              <p className="text-xs text-muted-foreground">Iniciativas (Portfolio), Pilotos y Ayudas/Subvenciones ya existentes en Innovation OS.</p>
            </div>
          </TabsContent>

          <TabsContent value="contenido" className="space-y-4 pt-4">
            <div className="space-y-2 rounded-lg border border-dashed border-emerald-300 bg-emerald-50/50 p-3">
              <Label>Texto de referencia (emails, notas, actas...)</Label>
              <Textarea
                rows={5} value={referenceText} onChange={(e) => setReferenceText(e.target.value)}
                placeholder="Pega aquí cualquier email, nota o texto largo que tengas sobre este proyecto — la IA lo usará como fuente principal para rellenar la ficha."
                data-testid="proposal-reference-text-input"
              />
              <div className="flex justify-end">
                <Button type="button" size="sm" variant="outline" onClick={handleAiDraft} disabled={drafting} data-testid="proposal-ai-draft-btn">
                  {drafting ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-2" />}
                  Rellenar ficha con IA
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Core del proyecto</Label>
              <Textarea rows={3} value={form.core_proyecto} onChange={(e) => set("core_proyecto", e.target.value)} placeholder="¿Qué es y para qué sirve el proyecto?" data-testid="proposal-core-input" />
            </div>
            <div className="space-y-2">
              <Label>Objetivos y beneficios esperados</Label>
              <Textarea rows={4} value={form.objetivos_beneficios} onChange={(e) => set("objetivos_beneficios", e.target.value)} data-testid="proposal-objetivos-input" />
            </div>
            <div className="space-y-2">
              <Label>Buenas prácticas y referencias</Label>
              <Textarea rows={3} value={form.buenas_practicas} onChange={(e) => set("buenas_practicas", e.target.value)} data-testid="proposal-buenas-practicas-input" />
            </div>
          </TabsContent>

          <TabsContent value="convocatoria" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Buscar convocatoria/ayuda en internet</Label>
              <div className="flex gap-2">
                <Input value={form.convocatoria.query_or_url} onChange={(e) => setConv("query_or_url", e.target.value)} placeholder="URL o nombre (ej: CDTI Neotec 2026)" data-testid="proposal-convocatoria-search-input" />
                <Button type="button" onClick={handleSearchConvocatoria} disabled={searching} data-testid="proposal-convocatoria-search-btn">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              {form.convocatoria.filled_by_ai && <p className="text-xs text-amber-600">Datos extraídos automáticamente — verifica antes de presentar.</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Organismo</Label><Input value={form.convocatoria.organismo} onChange={(e) => setConv("organismo", e.target.value)} data-testid="proposal-conv-organismo" /></div>
              <div className="space-y-2"><Label>Nombre convocatoria</Label><Input value={form.convocatoria.nombre} onChange={(e) => setConv("nombre", e.target.value)} data-testid="proposal-conv-nombre" /></div>
              <div className="space-y-2"><Label>Plazos</Label><Input value={form.convocatoria.plazos} onChange={(e) => setConv("plazos", e.target.value)} data-testid="proposal-conv-plazos" /></div>
              <div className="space-y-2"><Label>Presupuesto máximo</Label><Input value={form.convocatoria.presupuesto_maximo} onChange={(e) => setConv("presupuesto_maximo", e.target.value)} data-testid="proposal-conv-presupuesto" /></div>
              <div className="space-y-2"><Label>% Subvención</Label><Input value={form.convocatoria.porcentaje_subvencion} onChange={(e) => setConv("porcentaje_subvencion", e.target.value)} data-testid="proposal-conv-pct" /></div>
              <div className="space-y-2"><Label>URL oficial</Label><Input value={form.convocatoria.url} onChange={(e) => setConv("url", e.target.value)} data-testid="proposal-conv-url" /></div>
            </div>
            <div className="space-y-2">
              <Label>Condiciones</Label>
              <Textarea rows={3} value={form.convocatoria.condiciones} onChange={(e) => setConv("condiciones", e.target.value)} data-testid="proposal-conv-condiciones" />
            </div>
          </TabsContent>

          <TabsContent value="presupuesto" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Coste total estimado (€)</Label>
              <Input type="number" value={form.budget.coste_total} onChange={(e) => setBudget("coste_total", e.target.value)} placeholder="150000" data-testid="proposal-coste-total-input" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-emerald-700">OPEX {opexPct}%</span>
                <span className="font-medium text-lime-700">CAPEX {100 - opexPct}%</span>
              </div>
              <Slider value={[opexPct]} onValueChange={([v]) => { setBudget("opex_pct", v); setBudget("capex_pct", 100 - v); }} max={100} step={5} data-testid="proposal-opex-capex-slider" />
              <p className="text-xs text-muted-foreground">Divide el presupuesto entre gasto operativo (OPEX) y de capital/inversión (CAPEX).</p>
            </div>
            <div className="space-y-2">
              <Label>Riesgos</Label>
              <Textarea rows={3} value={form.riesgos} onChange={(e) => set("riesgos", e.target.value)} data-testid="proposal-riesgos-input" />
            </div>
            <div className="space-y-2">
              <Label>Cronograma / hitos</Label>
              <Textarea rows={3} value={form.cronograma} onChange={(e) => set("cronograma", e.target.value)} data-testid="proposal-cronograma-input" />
            </div>
            <div className="space-y-2">
              <Label>Equipo necesario</Label>
              <Textarea rows={3} value={form.equipo_necesario} onChange={(e) => set("equipo_necesario", e.target.value)} data-testid="proposal-equipo-input" />
            </div>
          </TabsContent>

          <TabsContent value="revision" className="space-y-3 pt-4" data-testid="proposal-review-tab">
            <div className="rounded-lg border p-4 space-y-2 bg-muted/30">
              <div className="font-semibold text-base">{form.title || "(sin título)"}</div>
              <div className="text-sm text-muted-foreground">{form.linea_estrategica || "Sin línea estratégica"}</div>
              <div className="text-sm">{form.linked_entities.length} entidad(es) vinculada(s)</div>
              <div className="text-sm">Presupuesto: {form.budget.coste_total ? `${form.budget.coste_total} € · OPEX ${opexPct}% / CAPEX ${100 - opexPct}%` : "Sin definir"}</div>
              {form.convocatoria.nombre && <div className="text-sm">Convocatoria: {form.convocatoria.nombre} ({form.convocatoria.organismo})</div>}
            </div>
            <div className="space-y-2">
              <Label>Notas adicionales</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="proposal-notes-input" />
            </div>
            <p className="text-xs text-muted-foreground">Al enviar a revisión, David Redondo recibirá un email con el PDF de la ficha para aprobar o rechazar.</p>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex-row justify-between sm:justify-between items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={goBack} disabled={stepIndex === 0} data-testid="proposal-step-back-btn">
            <ChevronLeft className="w-4 h-4 mr-1" />Atrás
          </Button>
          <div className="flex gap-2">
            {stepIndex < STEPS.length - 1 ? (
              <Button type="button" size="sm" onClick={goNext} data-testid="proposal-step-next-btn">Siguiente<ChevronRight className="w-4 h-4 ml-1" /></Button>
            ) : (
              <>
                <Button type="button" variant="outline" disabled={saving} onClick={() => handleSave(false)} data-testid="proposal-save-draft-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar borrador
                </Button>
                <Button type="button" disabled={saving} onClick={() => handleSave(true)} data-testid="proposal-send-review-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enviar a revisión
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
